/* ==========================================================================
   Compare tray.
   --------------------------------------------------------------------------
   Selection lives in sessionStorage and survives navigation, because comparing
   means moving between aisles — a tray that empties when you change page is
   worse than no tray. Capped at 4: beyond that the table stops being readable
   on any phone, and an unreadable comparison is not a comparison.
   ========================================================================== */

const KEY = 'opensku:compare'
const MAX = 4

const read = () => { try { return JSON.parse(sessionStorage.getItem(KEY)) || [] } catch { return [] } }
const write = (v) => { sessionStorage.setItem(KEY, JSON.stringify(v.slice(0, MAX))); paint() }

function paint() {
  const list = read()
  document.querySelectorAll('opensku-compare-toggle').forEach((el) => el.sync(list))
  const tray = document.querySelector('opensku-compare-tray')
  if (tray) tray.sync(list)
}

class CompareToggle extends HTMLElement {
  connectedCallback() {
    this.handle = this.getAttribute('handle')
    this.title_ = this.getAttribute('label') || ''
    this.render(read())
    this.addEventListener('click', (e) => {
      e.preventDefault()
      const list = read()
      const i = list.findIndex((x) => x.h === this.handle)
      if (i >= 0) list.splice(i, 1)
      else if (list.length >= MAX) { this.flash(); return }
      else list.push({
        h: this.handle, t: this.title_,
        u: this.dataset.url, p: this.dataset.price, i: this.dataset.img,
        // Capture the spec NOW — this is the only moment product and attributes coexist.
        s: (() => { try { return JSON.parse(this.dataset.spec) } catch { return null } })(),
      })
      write(list)
    })
  }
  sync(list) { this.render(list) }
  flash() { this.classList.add('is-full'); setTimeout(() => this.classList.remove('is-full'), 900) }
  render(list) {
    const on = list.some((x) => x.h === this.handle)
    this.innerHTML = `<button class="cmpbtn${on ? ' is-on' : ''}" aria-pressed="${on}"
      aria-label="${this.dataset.aria || 'Compare'}">${on ? '✓' : '⇄'}</button>`
  }
}

class CompareTray extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-rm]')
      if (rm) { const l = read(); write(l.filter((x) => x.h !== rm.dataset.rm)) }
      if (e.target.closest('[data-clear]')) write([])
    })
    paint()
  }
  sync(list) {
    if (!list.length) { this.hidden = true; return }
    this.hidden = false
    const url = `${this.dataset.url}?p=${list.map((x) => x.h).join(',')}`
    this.innerHTML = `
      <div class="tray">
        <span class="tray__n">${list.length}/${MAX}</span>
        <div class="tray__items">
          ${list.map((x) => `<button class="tray__chip" data-rm="${x.h}" title="${this.dataset.remove || 'Remove'}">${x.t}<span aria-hidden="true">×</span></button>`).join('')}
        </div>
        <a class="tray__go" href="${url}">${this.dataset.cta || 'Compare'}</a>
        <button class="tray__clear" data-clear>${this.dataset.clear || 'Clear'}</button>
      </div>`
  }
}

customElements.define('opensku-compare-toggle', CompareToggle)
customElements.define('opensku-compare-tray', CompareTray)
document.addEventListener('DOMContentLoaded', paint)


/* ------------------------------------------------------------------- table */

/**
 * Renders the comparison from what was captured at selection time.
 *
 * Two Shopify limitations force this shape, and both are worth remembering:
 *   1. Liquid cannot read the query string (request.url and request.query are empty), so the
 *      table cannot be server-rendered from ?p=handle,handle.
 *   2. /products/{handle}.js omits metafields, so the attributes cannot be fetched client-side.
 * Capturing at selection time satisfies both without an app proxy.
 */
class CompareTable extends HTMLElement {
  connectedCallback() {
    const list = read()
    const lang = (document.documentElement.lang || 'en').split('-')[0]
    const t = (k, d) => this.dataset[k] || d
    if (!list.length) { this.innerHTML = `<p class="cmp__empty">${t('empty', 'Pick products to compare.')}</p>`; return }

    // Union of rows across the chosen products, in first-seen order.
    const keys = []
    for (const it of list) for (const r of it.s?.r || []) if (!keys.includes(r.k)) keys.push(r.k)

    let neutral = {}
    try { neutral = JSON.parse(this.dataset.neutral || '{}') } catch {}

    /**
     * When products from different departments share an attribute KEY, their manifests may label
     * it differently — `subcategory` is "Cereal type" in breakfast-food and "Type" in disposables.
     * Taking the first product's label produced the row **"Cereal type: Gloves"**, which is not a
     * cosmetic slip: it asserts a category the product is not in.
     *
     * So: if every selected product agrees on the label, use it — that is the whole point of
     * category-aware comparison. If they disagree, fall back to the DEFAULT manifest's neutral
     * label, which is correct for all of them.
     */
    const labelFor = (k) => {
      const labels = new Set()
      let first = null
      for (const it of list) {
        const r = (it.s?.r || []).find((x) => x.k === k)
        if (!r) continue
        const l = lang === 'ar' ? (r.la || r.l) : r.l
        labels.add(l)
        if (first === null) first = l
      }
      if (labels.size <= 1) return first ?? k
      return neutral[k] || k
    }
    const cell = (it, k) => {
      const r = (it.s?.r || []).find((x) => x.k === k)
      // Not in this product's department manifest at all -> not applicable, an em dash.
      if (!r) return `<span class="cmp__na" title="${t('naTitle', '')}">—</span>`
      // Applicable but empty -> unknown, and it says so. Rendering this as blank would tell the
      // shopper the products are equivalent on a row where one is simply un-enriched.
      if (r.v === '' || r.v == null) return `<span class="cmp__unknown">${t('unknown', 'not recorded')}</span>`
      return String(r.v)
    }

    this.innerHTML = `
      <div class="cmp__scroll">
        <table class="cmp__table">
          <thead><tr><th scope="row"><span class="visually-hidden">${t('attribute', 'Attribute')}</span></th>
            ${list.map((it) => `<th scope="col">
              <a href="${it.u}">${it.i ? `<img src="${it.i}" alt="" width="100" height="100" loading="lazy">` : ''}
              <span class="cmp__name">${it.t}</span></a>
              <div class="cmp__price"><bdi>${it.p || ''}</bdi></div></th>`).join('')}
          </tr></thead>
          <tbody>
            ${keys.map((k) => `<tr><th scope="row">${labelFor(k)}</th>
              ${list.map((it) => `<td>${cell(it, k)}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      </div>
      <p class="cmp__legend">
        <span class="cmp__unknown">${t('unknown', 'not recorded')}</span> ${t('legendUnknown', '')} ·
        <span class="cmp__na">—</span> ${t('legendNa', '')}
      </p>`
  }
}
customElements.define('opensku-compare-table', CompareTable)
